import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Loader2 } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/oldConventionalSlice";
import { useLocation, useNavigate } from "react-router-dom";
import FilePreview from "../../../components/common/FilePreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const isValidFile = (file) =>
  file instanceof File || (typeof file === "string" && file.length > 0);

const particularsSchema = (user) =>
  z
    .object({
      applicant_name: z.string().min(1, "Applicant name is required"),
      membership: z.string().min(1, "Membership Type is required"),
      name_of_proprietor: z.string().optional(),
      father_name: z.string().min(1, "Father/Husband name is required"),
      date_of_birth: z.string().min(1, "Date of Birth is required"),
      age: z.string().optional(),
      date_of_incorporation: z.string().optional(),
      gender: z.string().min(1, "Gender is required"),
      community_id: z.string().min(1, "Community is required"),
      contact_number: z
        .string()
        .min(1, "Contact number is required")
        .regex(/^[0-9]{10}$/, "Contact number must be 10 digits"),
      email: z
        .string()
        .email("Invalid email address")
        .min(1, "Email is required"),
      web_address: z.string().optional(),
      aadhaar_number: z
        .string()
        .min(1, "Aadhaar number is required")
        .regex(
          /^[0-9]{4}-[0-9]{4}-[0-9]{4}$/,
          "Aadhaar must be in format XXXX-XXXX-XXXX"
        ),
      residential_address: z.string().min(1, "Residential Address is required"),
      city: z.string().min(1, "City is required"),
      district_id: z.string().min(1, "District is required"),
      state: z.string().min(1, "State is required"),
      residential_pincode: z
        .string()
        .min(1, "Residential Pincode is required")
        .regex(/^[0-9]{6}$/, "Residential Pincode must be 6 digits"),
      aadhaar_copy: z.any().refine(isValidFile, "Aadhaar Upload is required"),
      photo: z.any().refine(isValidFile, "Present Photo is required"),
      incorporation_certificate: z.any().optional(),
      partnership_deed: z.any().optional(),
      pan_number: z
        .string()
        .optional()
        .refine(
          (val) => !val || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val),
          "Invalid PAN Number"
        ),

      pan_upload: z.any().optional(),
      gst_number: z
        .string()
        .optional()
        .refine(
          (val) =>
            !val ||
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
              val
            ),
          "Invalid GST Number"
        ),
      gst_upload: z.any().optional(),
    })
    .superRefine((data, ctx) => {
      if (
        user?.organization_type == "Private Firm / Partnership" ||
        user?.organization_type == "Proprietor"
      ) {
        if (!isValidFile(data.incorporation_certificate)) {
          ctx.addIssue({
            path: ["incorporation_certificate"],
            code: z.ZodIssueCode.custom,
            message: "Incorporation certificate attachment is required",
          });
        }

        if (!isValidFile(data.pan_upload)) {
          ctx.addIssue({
            path: ["pan_upload"],
            code: z.ZodIssueCode.custom,
            message: "PAN Document attachment is required",
          });
        }

        if (!isValidFile(data.gst_upload)) {
          ctx.addIssue({
            path: ["gst_upload"],
            code: z.ZodIssueCode.custom,
            message: "GST Document attachment is required",
          });
        }

        if (!data.date_of_incorporation) {
          ctx.addIssue({
            path: ["date_of_incorporation"],
            code: z.ZodIssueCode.custom,
            message: "Date of Incorporation is required",
          });
        }

        if (!data.pan_number) {
          ctx.addIssue({
            path: ["pan_number"],
            code: z.ZodIssueCode.custom,
            message: "PAN Number is required",
          });
        }

        if (!data.gst_number) {
          ctx.addIssue({
            path: ["gst_number"],
            code: z.ZodIssueCode.custom,
            message: "GST Number is required",
          });
        }

        if (!data.name_of_proprietor) {
          ctx.addIssue({
            path: ["name_of_proprietor"],
            code: z.ZodIssueCode.custom,
            message: "Name of proprietor is required",
          });
        }
      }
    });

const PersonalDetails = ({ data, districts, communities, file_name = "" }) => {
  const [filePreviews, setFilePreviews] = useState({});
  const location = useLocation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [districtNoteOpen, setDistrictNoteOpen] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(particularsSchema(user)),
    defaultValues: {
      applicant_name: data?.applicant_name ?? user?.name ?? "",
      membership: data?.membership ?? user?.organization_type ?? "",
      name_of_proprietor: data?.name_of_proprietor ?? "",
      father_name: data?.father_name ?? "",
      date_of_birth: data?.date_of_birth
        ? new Date(data.date_of_birth).toISOString().split("T")[0]
        : "",
      age: data?.date_of_birth
        ? (() => {
            const birthDate = new Date(data.date_of_birth);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (
              monthDiff < 0 ||
              (monthDiff === 0 && today.getDate() < birthDate.getDate())
            ) {
              age--;
            }
            return age.toString();
          })()
        : "",
      date_of_incorporation: data?.date_of_incorporation
        ? new Date(data.date_of_incorporation).toISOString().split("T")[0]
        : "",
      gender: data?.gender ?? "",
      community_id: data?.community_id ? String(data.community_id) : "",
      contact_number: data?.contact_number ?? user?.phone ?? "",
      email: data?.email ?? user?.email ?? "",
      web_address: data?.web_address ?? "",
      aadhaar_number: data?.aadhaar_number ?? "",
      residential_address: data?.residential_address ?? "",
      city: data?.city ?? "",
      district_id: data?.district_id ? String(data.district_id) : "",
      state: data?.state ?? "Tamil Nadu",
      residential_pincode: data?.residential_pincode ?? "",
      pan_number: data?.pan_number ?? "",
      gst_number: data?.gst_number ?? "",
      photo: data?.photo ?? undefined,
      aadhaar_copy: data?.aadhaar_copy ?? undefined,
      incorporation_certificate: data?.incorporation_certificate ?? undefined,
      pan_upload: data?.pan_upload ?? undefined,
      gst_upload: data?.gst_upload ?? undefined,
      partnership_deed: data?.partnership_deed ?? undefined,
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data) {
      const previews = {
        photo: data?.photo ?? undefined,
        aadhaar_copy: data?.aadhaar_copy ?? undefined,
        incorporation_certificate: data?.incorporation_certificate ?? undefined,
        pan_upload: data?.pan_upload ?? undefined,
        gst_upload: data?.gst_upload ?? undefined,
        partnership_deed: data?.partnership_deed ?? undefined,
      };

      setFilePreviews(previews);
    }
  }, [data]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      if (name === "photo" && file.type === "application/pdf") {
        toast({
          title: "Invalid File Type",
          description:
            "PDF files are not allowed for photo upload. Please upload an image file.",
          variant: "destructive",
        });
        return;
      }

      setValue(name, file, { shouldValidate: true });

      if (name === "photo" && file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, photo: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      submitData.append("id", data?.id || "");

      const response = await api.post("/modernization/personal", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      dispatch(setData(response?.data?.data || {}));

      if (buttonType === "next") {
        // Scroll to top before navigation
        window.scrollTo({ top: 0, behavior: "smooth" });
        dispatch(setCurrentTab("loom"));
      }
      toast({
        title: "Success!",
        description: "Application Saved successfully",
        variant: "success",
        duration: 2000,
      });

      searchParams.delete("tab");
      nav(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );

      dispatch(setUserInitiatedNav(true));
      if (buttonType === "next") {
        dispatch(setCurrentTab("loom"));
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });

    if (fieldName === "date_of_birth" && value) {
      const birthDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      setValue("age", age.toString(), { shouldValidate: true });
    }

    if (fieldName === "aadhaar_number") {
      const cleaned = value.replace(/\D/g, "");
      if (cleaned.length <= 12) {
        const formatted = cleaned.replace(
          /(\d{4})(\d{0,4})(\d{0,4})/,
          (match, p1, p2, p3) => {
            let result = p1;
            if (p2) result += `-${p2}`;
            if (p3) result += `-${p3}`;
            return result;
          }
        );
        setValue("aadhaar_number", formatted, { shouldValidate: true });
      }
    }

    if (fieldName === "contact_number") {
      const cleaned = value.replace(/\D/g, "").slice(0, 10);
      setValue("contact_number", cleaned, { shouldValidate: true });
    }

    if (fieldName === "pincode") {
      const cleaned = value.replace(/\D/g, "").slice(0, 6);
      setValue("pincode", cleaned, { shouldValidate: true });
    }
  };

  const today = new Date();
  const year = today.getFullYear() - 18;
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const maxDate = `${year}-${month}-${day}`;

  const onSubmitClick = async (e, type) => {
    e.preventDefault();
    setButtonType(type); // Save button type first
    // console.log("🖱️ Submit button clicked. Type:", type);

    const isValid = await trigger();
    // console.log("✅ Validation result:", isValid);
    // console.log("⚠️ Validation errors:", errors);

    if (isValid) {
      // console.log("🎯 Form is valid, calling handleSubmitSpv...");
      // await form.handleSubmit(handleSubmitSpv)();
    } else {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        variant: "destructive",
      });
    }
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Personal Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="applicant_name" required>
                Name of the Applicant / Name of the Organization{" "}
              </Label>
              <Input
                id="applicant_name"
                {...register("applicant_name")}
                onChange={(e) =>
                  handleInputChange("applicant_name", e.target.value)
                }
                className={`${
                  errors.applicant_name ? "border-red-500" : ""
                } cursor-not-allowed bg-muted`}
                style={{ pointerEvents: "none" }}
                readOnly
              />
              {errors.applicant_name && (
                <p className="text-sm text-red-500">
                  {errors.applicant_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="membership" required>
                Organization Type
              </Label>
              <Input
                id="membership"
                {...register("membership")}
                onChange={(e) =>
                  handleInputChange("membership", e.target.value)
                }
                className={`${
                  errors.membership ? "border-red-500" : ""
                } cursor-not-allowed bg-muted`}
                style={{ pointerEvents: "none" }}
                readOnly
              />
              {errors.membership && (
                <p className="text-sm text-red-500">
                  {errors.membership.message}
                </p>
              )}
            </div>

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") && (
              <div className="space-y-2">
                <Label htmlFor="name_of_proprietor" required>
                  Name of Proprietor / Partners / Managing Director{" "}
                </Label>
                <Input
                  id="name_of_proprietor"
                  {...register("name_of_proprietor")}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z.\s]/g, "");
                    handleInputChange("name_of_proprietor", value);
                  }}
                  className={`${
                    errors.name_of_proprietor ? "border-red-500" : ""
                  }`}
                />
                {errors.name_of_proprietor && (
                  <p className="text-sm text-red-500">
                    {errors.name_of_proprietor.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="father_name" required>
                Father Name/ Spouse Name
              </Label>
              <Input
                id="father_name"
                {...register("father_name")}
                onChange={(e) => {
                  const value = e.target.value;
                  const filteredValue = value.replace(/[^a-zA-Z.\s]/g, "");
                  handleInputChange("father_name", filteredValue);
                }}
                className={errors.father_name ? "border-red-500" : ""}
              />
              {errors.father_name && (
                <p className="text-sm text-red-500">
                  {errors.father_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_birth" required>
                Date of Birth
              </Label>
              <Input
                id="date_of_birth"
                type="date"
                {...register("date_of_birth")}
                onChange={(e) =>
                  handleInputChange("date_of_birth", e.target.value)
                }
                className={errors.date_of_birth ? "border-red-500" : ""}
                max={maxDate}
              />
              {errors.date_of_birth && (
                <p className="text-sm text-red-500">
                  {errors.date_of_birth.message}
                </p>
              )}
            </div>

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") && (
              <div className="space-y-2">
                <Label htmlFor="date_of_incorporation" required>
                  Date of Incorporation
                </Label>
                <Input
                  id="date_of_incorporation"
                  type="date"
                  {...register("date_of_incorporation")}
                  onChange={(e) =>
                    handleInputChange("date_of_incorporation", e.target.value)
                  }
                  className={
                    errors.date_of_incorporation ? "border-red-500" : ""
                  }
                />
                {errors.date_of_incorporation && (
                  <p className="text-sm text-red-500">
                    {errors.date_of_incorporation.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="text"
                {...register("age")}
                className={`${errors.name ? "border-red-500" : ""} ${
                  user?.name ? "cursor-not-allowed bg-muted" : ""
                }`}
                style={{ pointerEvents: "none" }}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gender" required>
                Gender
              </Label>
              <Select
                value={formData.gender}
                onValueChange={(value) =>
                  setValue("gender", value, { shouldValidate: true })
                }
              >
                <SelectTrigger
                  className={errors.gender ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {errors.gender && (
                <p className="text-sm text-red-500">{errors.gender.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="community_id" required>
                Community
              </Label>
              <Select
                value={formData.community_id}
                onValueChange={(value) =>
                  setValue("community_id", value, { shouldValidate: true })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Community" />
                </SelectTrigger>
                <SelectContent>
                  {communities &&
                    communities.map((community) => (
                      <SelectItem
                        key={community.id}
                        value={String(community.id)}
                      >
                        {community.name || `community_id ${community.id}`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {errors.community_id && (
                <p className="text-sm text-red-500">
                  {errors.community_id.message}
                </p>
              )}
            </div>

            {/* Contact Number */}
            <div className="space-y-2">
              <Label htmlFor="contact_number" required>
                Contact Number
              </Label>
              <Input
                id="contact_number"
                type="tel"
                {...register("contact_number")}
                onChange={(e) =>
                  handleInputChange("contact_number", e.target.value)
                }
                className={
                  errors.contact_number
                    ? "border-red-500 cursor-not-allowed bg-muted"
                    : " cursor-not-allowed bg-muted"
                }
                placeholder="10-digit number"
                style={{ pointerEvents: "none" }}
                readOnly
              />
              {errors.contact_number && (
                <p className="text-sm text-red-500">
                  {errors.contact_number.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" required>
                Email ID
              </Label>
              <Input
                id="email"
                type="text"
                {...register("email")}
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`${errors.email ? "border-red-500" : ""} ${
                  user?.email ? "cursor-not-allowed bg-muted" : ""
                }`}
                readOnly
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Web Address */}
            <div className="space-y-2">
              <Label htmlFor="web_address">Web Address (optional)</Label>
              <Input
                id="web_address"
                type="text"
                {...register("web_address")}
                onChange={(e) =>
                  handleInputChange("web_address", e.target.value)
                }
                className={errors.web_address ? "border-red-500" : ""}
              />
              {errors.web_address && (
                <p className="text-sm text-red-500">
                  {errors.web_address.message}
                </p>
              )}
            </div>

            {/* Aadhaar Number */}
            <div className="space-y-2">
              <Label htmlFor="aadhaar_number" required>
                Aadhaar Number
              </Label>
              <Input
                id="aadhaar_number"
                {...register("aadhaar_number")}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  if (value.length > 12) value = value.slice(0, 12);

                  const formatted = value.replace(
                    /(\d{4})(\d{0,4})(\d{0,4})/,
                    (match, p1, p2, p3) => {
                      let result = p1;
                      if (p2) result += `-${p2}`;
                      if (p3) result += `-${p3}`;
                      return result;
                    }
                  );

                  handleInputChange("aadhaar_number", formatted);
                }}
                className={errors.aadhaar_number ? "border-red-500" : ""}
                placeholder="XXXX-XXXX-XXXX"
              />
              {errors.aadhaar_number && (
                <p className="text-sm text-red-500">
                  {errors.aadhaar_number.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="residential_address" required>
                Residential Address
              </Label>
              <Textarea
                id="residential_address"
                {...register("residential_address")}
                rows={2}
              />
              {errors.residential_address && (
                <p className="text-sm text-red-500">
                  {errors.residential_address.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" required>
                City
              </Label>
              <Input
                id="city"
                {...register("city")}
                onChange={(e) => handleInputChange("city", e.target.value)}
                className={errors.city ? "border-red-500" : ""}
              />
              {errors.city && (
                <p className="text-sm text-red-500">{errors.city.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="district_id" required>
                District
              </Label>
              <Select
                value={formData.district_id}
                onValueChange={(value) => {
                  if (value == "19") {
                    setDistrictNoteOpen(true);
                  }
                  setValue("district_id", value, { shouldValidate: true });
                }}
              >
                <SelectTrigger
                  className={errors.district_id ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select District" />
                </SelectTrigger>
                <SelectContent>
                  {districts &&
                    districts.map((district) => (
                      <SelectItem key={district.id} value={String(district.id)}>
                        {district.name || `District ${district.id}`}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {errors.district_id && (
                <p className="text-sm text-red-500">
                  {errors.district_id.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="state" required>
                State
              </Label>
              <Input
                id="state"
                readOnly
                {...register("state")}
                onChange={(e) => handleInputChange("state", e.target.value)}
                className={
                  errors.state
                    ? "border-red-500"
                    : "bg-muted cursor-not-allowed"
                }
                style={{ pointerEvents: "none" }}
              />
              {errors.state && (
                <p className="text-sm text-red-500">{errors.state.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="residential_pincode" required>
                Residential Pincode
              </Label>
              <Input
                id="residential_pincode"
                {...register("residential_pincode")}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  handleInputChange("residential_pincode", value);
                }}
                className={errors.residential_pincode ? "border-red-500" : ""}
                placeholder="6-digit pincode"
              />
              {errors.residential_pincode && (
                <p className="text-sm text-red-500">
                  {errors.residential_pincode.message}
                </p>
              )}
            </div>

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="pan_number" required>
                    PAN Number
                  </Label>
                  <Input
                    id="pan_number"
                    {...register("pan_number")}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 10);
                      handleInputChange("pan_number", value);
                    }}
                    className={errors.pan_number ? "border-red-500" : ""}
                  />
                  {errors.pan_number && (
                    <p className="text-sm text-red-500">
                      {errors.pan_number.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pan_upload" required>
                    PAN Document Upload
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="pan_upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) =>
                        handleFileUpload("pan_upload", e.target.files[0])
                      }
                      className={errors.pan_upload ? "border-red-500" : ""}
                    />

                    <FilePreview
                      fieldName="pan_upload"
                      preview={filePreviews.pan_upload}
                      file={
                        formData.pan_upload instanceof File
                          ? formData.pan_upload
                          : null
                      }
                      existingFile={
                        typeof filePreviews.pan_upload === "string"
                          ? filePreviews.pan_upload
                          : null
                      }
                      onRemove={handleRemoveFile}
                    />

                    {errors.pan_upload && (
                      <p className="text-sm text-red-500">
                        {errors.pan_upload.message}
                      </p>
                    )}

                    <p className="ms-1 text-[12px] text-muted-foreground">
                      Supported only PDF, JPG, JPEG, PNG Files max size 2MB
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_number" required>
                    GST Number
                  </Label>
                  <Input
                    id="gst_number"
                    {...register("gst_number")}
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 15);
                      handleInputChange("gst_number", value);
                    }}
                    className={errors.gst_number ? "border-red-500" : ""}
                  />
                  {errors.gst_number && (
                    <p className="text-sm text-red-500">
                      {errors.gst_number.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_upload" required>
                    GST Document Upload
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="gst_upload"
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) =>
                        handleFileUpload("gst_upload", e.target.files[0])
                      }
                      className={errors.gst_upload ? "border-red-500" : ""}
                    />

                    <FilePreview
                      fieldName="gst_upload"
                      preview={filePreviews.gst_upload}
                      file={
                        formData.gst_upload instanceof File
                          ? formData.gst_upload
                          : null
                      }
                      existingFile={
                        typeof filePreviews.gst_upload === "string"
                          ? filePreviews.gst_upload
                          : null
                      }
                      onRemove={handleRemoveFile}
                    />

                    {errors.gst_upload && (
                      <p className="text-sm text-red-500">
                        {errors.gst_upload.message}
                      </p>
                    )}

                    <p className="ms-1 text-[12px] text-muted-foreground">
                      Supported only PDF, JPG, JPEG, PNG Files max size 2MB
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="photo" required>
                Applicant Present Photo
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="photo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload("photo", e.target.files[0])}
                  className={errors.photo ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="photo"
                  preview={filePreviews.photo}
                  file={formData.photo instanceof File ? formData.photo : null}
                  existingFile={
                    typeof filePreviews.photo === "string"
                      ? filePreviews.photo
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.photo && (
                  <p className="text-sm text-red-500">{errors.photo.message}</p>
                )}

                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only JPG, JPEG, PNG Files max size 2MB
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="aadhaar_copy" required>
                Aadhaar Uploads
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="aadhaar_copy"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) =>
                    handleFileUpload("aadhaar_copy", e.target.files[0])
                  }
                  className={errors.aadhaar_copy ? "border-red-500" : ""}
                />

                <FilePreview
                  fieldName="aadhaar_copy"
                  preview={filePreviews.aadhaar_copy}
                  file={
                    formData.aadhaar_copy instanceof File
                      ? formData.aadhaar_copy
                      : null
                  }
                  existingFile={
                    typeof filePreviews.aadhaar_copy === "string"
                      ? filePreviews.aadhaar_copy
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.aadhaar_copy && (
                  <p className="text-sm text-red-500">
                    {errors.aadhaar_copy.message}
                  </p>
                )}
                <p className="ms-1 text-[12px] text-muted-foreground">
                  Supported only PDF, PNG, JPG, JPEG Files max size 2MB
                </p>
              </div>
            </div>

            {(user?.organization_type == "Private Firm / Partnership" ||
              user?.organization_type == "Proprietor") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="incorporation_certificate" required>
                    Incorporation Certificate Upload{" "}
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="incorporation_certificate"
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) =>
                        handleFileUpload(
                          "incorporation_certificate",
                          e.target.files[0]
                        )
                      }
                      className={
                        errors.incorporation_certificate ? "border-red-500" : ""
                      }
                    />

                    <FilePreview
                      fieldName="incorporation_certificate"
                      preview={filePreviews.incorporation_certificate}
                      file={
                        formData.incorporation_certificate instanceof File
                          ? formData.incorporation_certificate
                          : null
                      }
                      existingFile={
                        typeof filePreviews.incorporation_certificate ===
                        "string"
                          ? filePreviews.incorporation_certificate
                          : null
                      }
                      onRemove={handleRemoveFile}
                    />

                    {errors.incorporation_certificate && (
                      <p className="text-sm text-red-500">
                        {errors.incorporation_certificate.message}
                      </p>
                    )}
                    <p className="ms-1 text-[12px] text-muted-foreground">
                      Supported only PDF, PNG, JPG, JPEG Files max size 2MB
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="partnership_deed">
                    Partnership Deed Upload
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="partnership_deed"
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) =>
                        handleFileUpload("partnership_deed", e.target.files[0])
                      }
                      className={
                        errors.partnership_deed ? "border-red-500" : ""
                      }
                    />

                    <FilePreview
                      fieldName="partnership_deed"
                      preview={filePreviews.partnership_deed}
                      file={
                        formData.partnership_deed instanceof File
                          ? formData.partnership_deed
                          : null
                      }
                      existingFile={
                        typeof filePreviews.partnership_deed === "string"
                          ? filePreviews.partnership_deed
                          : null
                      }
                      onRemove={handleRemoveFile}
                    />

                    {errors.partnership_deed && (
                      <p className="text-sm text-red-500">
                        {errors.partnership_deed.message}
                      </p>
                    )}
                    <p className="ms-1 text-[12px] text-muted-foreground">
                      Supported only PDF, PNG, JPG, JPEG Files max size 2MB
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      <Dialog open={districtNoteOpen} onOpenChange={setDistrictNoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Important Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            If You are from Edappadi and Sankagiri Taluk of Salem then please
            select the <b>Salem(Edappadi & Sankagiri Taluk)</b> from dropdown
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setDistrictNoteOpen(false)}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
};

export default PersonalDetails;
